import jsPDF from 'jspdf';
import logo from '../images/logo.png';

const GIALLO = '#ffcc00';
const BLU = '#36a9e1';
const VIOLA = '#3b235a';
const TEAL = '#3eb297';

const getBase64ImageFromURL = async (url) => {
  const res = await fetch(url);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/**
 * PDF riassuntivo pagina sponsor (brochure per potenziali partner).
 * @param {{
 *   projects?: Array<{ title: string, descrizione?: string }>,
 *   communityImpact: object,
 *   benefitCards: Array<{ title: string, desc: string }>,
 *   collaborationTypes: Array<{ title: string, desc: string }>,
 *   whoWeLookFor: string[],
 * }} data
 */
export const generateSponsorSummaryPDF = async (data) => {
  const {
    projects = [],
    communityImpact,
    benefitCards = [],
    collaborationTypes = [],
    whoWeLookFor = [],
  } = data;

  let logoData = '';
  try {
    logoData = await getBase64ImageFromURL(logo);
  } catch (e) {
    console.warn('Logo non caricato per PDF sponsor:', e);
  }

  // eslint-disable-next-line new-cap
  const doc = new jsPDF('p', 'pt', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 44;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  const checkPageBreak = (space = 24) => {
    if (y + space > pageHeight - 56) {
      doc.addPage();
      y = margin;
      return true;
    }
    return false;
  };

  const addSectionTitle = (title) => {
    checkPageBreak(36);
    doc.setFillColor(TEAL);
    doc.rect(margin, y - 4, 4, 18, 'F');
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(VIOLA);
    doc.text(title, margin + 12, y + 10);
    y += 28;
  };

  const addParagraph = (text, fontSize = 10) => {
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor('#333333');
    const lines = doc.splitTextToSize(text, contentWidth);
    lines.forEach((line) => {
      checkPageBreak(14);
      doc.text(line, margin, y);
      y += 13;
    });
    y += 6;
  };

  const addBulletList = (items) => {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor('#333333');
    items.forEach((item) => {
      const lines = doc.splitTextToSize(`• ${item}`, contentWidth - 8);
      lines.forEach((line, idx) => {
        checkPageBreak(14);
        doc.text(line, margin + (idx === 0 ? 0 : 8), y);
        y += 13;
      });
      y += 2;
    });
    y += 4;
  };

  const addLabeledBlock = (label, value) => {
    checkPageBreak(20);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(VIOLA);
    doc.text(label, margin, y);
    y += 14;
    addParagraph(value, 10);
  };

  // —— Header CIL ——
  doc.setFillColor(GIALLO);
  doc.rect(0, 0, pageWidth, 120, 'F');
  if (logoData) {
    doc.addImage(logoData, 'PNG', pageWidth - margin - 95, 18, 95, 32);
  }
  doc.setFontSize(11);
  doc.setTextColor('#000000');
  doc.setFont('helvetica', 'bold');
  doc.text('CIL – Creatività in Libertà APS', margin, 28);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(
    'Via Carlo Alberto 7, 10023 Chieri (TO) · info@cilassociazione.it',
    margin,
    44
  );
  doc.text('www.cilassociazione.it · Pagina Sponsor', margin, 58);

  y = 140;
  doc.setLineWidth(1);
  doc.setDrawColor(BLU);
  doc.line(margin, y, pageWidth - margin, y);
  y += 28;

  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(VIOLA);
  doc.text('Diventa sponsor di CIL', pageWidth / 2, y, { align: 'center' });
  y += 22;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor('#444444');
  doc.text(
    `Riepilogo partnership · ${new Date().toLocaleDateString('it-IT')}`,
    pageWidth / 2,
    y,
    {
      align: 'center',
    }
  );
  y += 32;

  addSectionTitle('Chi siamo e perché collaborare');
  addParagraph(
    "CIL è un'associazione di promozione sociale: organizziamo eventi di gioco, laboratori creativi, attività per ragazzi e progetti educativi a Chieri e in provincia. Un partner sponsor ci aiuta a realizzare più iniziative e, in cambio, raggiunge una community attiva e coinvolta."
  );

  if (communityImpact) {
    addSectionTitle(
      `Impatto sulla community (${communityImpact.period || ''})`
    );
    addParagraph(communityImpact.intro || '');

    const kpiRows = (communityImpact.kpis || []).map(
      (k) => `${k.value} — ${k.label}: ${k.detail}`
    );
    if (kpiRows.length) addBulletList(kpiRows);

    if (communityImpact.sociComposition?.length) {
      checkPageBreak(24);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(VIOLA);
      doc.text('Composizione soci:', margin, y);
      y += 14;
      communityImpact.sociComposition.forEach((row) => {
        checkPageBreak(14);
        doc.setFont('helvetica', 'normal');
        doc.text(`${row.label}: ${row.percent}%`, margin + 8, y);
        y += 13;
      });
      y += 6;
    }

    if (communityImpact.activityTypes?.length) {
      addBulletList(
        communityImpact.activityTypes.map((a) => `${a.label} (${a.count})`)
      );
    }

    if (communityImpact.reach) {
      addLabeledBlock('Portata media eventi', communityImpact.reach);
    }
  }

  addSectionTitle('Cosa offre una partnership');
  benefitCards.forEach((b) => {
    checkPageBreak(28);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(VIOLA);
    doc.text(b.title, margin, y);
    y += 13;
    addParagraph(b.desc, 9);
  });

  addSectionTitle('Chi cerchiamo');
  addBulletList(whoWeLookFor);

  addSectionTitle('Come possiamo collaborare');
  collaborationTypes.forEach((c) => {
    checkPageBreak(32);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(VIOLA);
    doc.text(c.title, margin, y);
    y += 13;
    addParagraph(c.desc, 9);
  });

  addSectionTitle('Progetti da sostenere');
  if (projects.length === 0) {
    addParagraph(
      'Al momento non risultano progetti attivi in questa brochure. Contattaci per aggiornamenti o per proporre una collaborazione.'
    );
  } else {
    projects.forEach((p, i) => {
      checkPageBreak(40);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(VIOLA);
      doc.text(`${i + 1}. ${p.title || 'Progetto'}`, margin, y);
      y += 14;
      const desc = (p.descrizione || '').trim();
      if (desc) {
        addParagraph(desc, 9);
      } else {
        y += 4;
      }
    });
  }

  addSectionTitle('Contatti');
  addParagraph(
    'Per diventare sponsor o approfondire una partnership: info@cilassociazione.it · Tel. +39 353 468 2493 · www.cilassociazione.it/sponsor'
  );

  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor('#888888');
    doc.setFont('helvetica', 'normal');
    doc.text(
      `CIL Sponsor – pagina ${i} di ${pageCount}`,
      pageWidth / 2,
      pageHeight - 28,
      { align: 'center' }
    );
  }

  const dateStr = new Date().toISOString().slice(0, 10);
  doc.save(`CIL_Riepilogo_Sponsor_${dateStr}.pdf`);
};
